import { useEffect, useState } from 'react';

import { useEndpoint } from 'api';
import StorePopover from 'components/StorePopover';
import {
	type StoreIdName,
	useSelectedStore,
	useSelectedStoreInfo,
} from 'contexts';
import type { Store } from 'models/api';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	storePopoverChangeStoreTitle: string;
	storePopoverSelectStoreTitle: string;
}

/** Popover for the 'home store' selector */
export default function StoreSelectorPopover({
	isOpen,
	onClose,
	storePopoverChangeStoreTitle,
	storePopoverSelectStoreTitle,
}: Props) {
	const { selectedStore, updateSelectedStore } = useSelectedStore();

	const { storeInfo } = useSelectedStoreInfo();
	const [storeDetails, setStoreDetails] = useState<Store | undefined>(
		undefined,
	);
	const [shouldFetchNearbyStores, setShouldFetchNearbyStores] = useState(false);
	const [shouldFetchAllStores, setShouldFetchAllStores] = useState(false);
	const onStoreSelect = (store: StoreIdName) => {
		updateSelectedStore(store);
		onClose();
	};

	const { data: allStores, isLoading: isLoadingAllStores } = useEndpoint(
		'GetStores',
		{
			isActive: shouldFetchAllStores,
		},
	);

	const { data: nearbyStores, isLoading: isLoadingNearbyStores } = useEndpoint(
		selectedStore?.id
			? {
					name: 'GetNearbyStores',
					queryParams: { storeId: selectedStore?.id },
				}
			: null,
		{ isActive: shouldFetchNearbyStores },
	);

	useEffect(() => {
		if (isOpen) {
			if (selectedStore) {
				setShouldFetchNearbyStores(true);
				setShouldFetchAllStores(true);
			} else {
				setShouldFetchAllStores(true);
			}
		}
	}, [isOpen, selectedStore]);

	useEffect(() => {
		if (storeInfo) setStoreDetails(storeInfo);
	}, [storeInfo]);

	const isLoading =
		!storeDetails && (isLoadingAllStores || isLoadingNearbyStores);
	return (
		<StorePopover
			baseId="store-selctor"
			title={
				selectedStore?.id
					? storePopoverChangeStoreTitle
					: storePopoverSelectStoreTitle
			}
			selectedStoreId={selectedStore?.id}
			isLoading={isLoading}
			isOpen={isOpen}
			onClose={onClose}
			allStores={allStores}
			onStoreSelectClick={(store) => {
				onStoreSelect({ id: store.id, name: store.name });
			}}
			nearbyStores={nearbyStores}
			storeDetails={storeDetails}
			onBackClick={() => {
				setStoreDetails(undefined);
			}}
		/>
	);
}
StoreSelectorPopover.displayName = 'StoreSelectorPopover';
